import React from 'react';
import {ConstraintDataPlugin, ConstraintPluginProps} from '../ConstraintDataPlugin';
import {
    ConstraintType,
    DataTypeConstraintDto,
    DateTimeRangeConstraintDataDto,
} from '../../../../../../common/apis/datatype/DataTypeConstraintDto';
import {CoreDataType} from "../../../../../../common/apis/datatype/DataTypeDefinitionDto";
import {DataTypeTranslationKey} from "../../DataTypeTranslationKey";
import {DateTimeRangeConstraintPluginForm} from "./DateTimeRangeConstraintPluginForm";
import {TimeFormatsConverter} from "./TimeFormatsConverter";
import {ConstraintValueValidationPlugin} from "../ConstraintValueValidationPlugin";
import {TranslationFunction} from "../../../../../../store/localization/TranslMessasge";
import {CommonTranslationKey} from "../../../CommonTranslationKey";
import {StereotypesTranslationKey} from "../../../stereotypes/StereotypesTranslationKey";
import {DateTimeFormatter} from "../../../../../../common/DateTimeFormatter";

export class DateTimeRangeConstraintPlugin implements ConstraintDataPlugin<DateTimeRangeConstraintDataDto>, ConstraintValueValidationPlugin {

    readonly type = ConstraintType.DATE_TIME_RANGE;

    getSupportedDataTypes(): CoreDataType[] {
        return [
            CoreDataType.DATETIME
        ];
    }

    getInitialData(): DateTimeRangeConstraintDataDto {
        return {
            type: ConstraintType.DATE_TIME_RANGE,
            dateTimeFrom: null,
            dateTimeTo: null
        };
    }

    render(props: ConstraintPluginProps<DateTimeRangeConstraintDataDto>): React.ReactElement {
        return <DateTimeRangeConstraintPluginForm {...props} />;
    }

    validate(data: DateTimeRangeConstraintDataDto, coreDataType: CoreDataType): Record<string, string> {
        const errors: Record<string, string> = {};

        if (!data.dateTimeFrom && !data.dateTimeTo) {
            errors.dateTimeFrom = DataTypeTranslationKey.AT_LEAST_ONE_OF_DATE_TIME_FROM_OR_DATE_TIME_TO_MUST_BE_SPECIFIED;
        }
        let convertedFrom = TimeFormatsConverter.offsetDateTimeStringToDayjs(data.dateTimeFrom);
        let convertedTo = TimeFormatsConverter.offsetDateTimeStringToDayjs(data.dateTimeTo);

        if (data.dateTimeFrom != null && convertedFrom === null) {
            errors.dateTimeFrom = DataTypeTranslationKey.INVALID_TIME_FROM;
        }
        if (data.dateTimeTo != null && convertedTo === null) {
            errors.dateTimeTo = DataTypeTranslationKey.INVALID_TIME_TO;
        } else if (convertedFrom && convertedTo?.isBefore(data.dateTimeFrom)) {
            errors.dateTimeFrom = DataTypeTranslationKey.DATE_TIME_FROM_VALUE_MUST_BE_LESS_THAN_OR_EQUAL_TO_DATE_TIME_TO;
        }

        return errors;
    }

    validateValue(value: any, attributeId: string, constraint: DataTypeConstraintDto, t: TranslationFunction): Record<string, string> {
        const constraintData = constraint.constraintData as DateTimeRangeConstraintDataDto;
        const {dateTimeFrom, dateTimeTo} = constraintData;

        if (!value) {
            return {
                [attributeId]: t(CommonTranslationKey.FILL_OUT_THIS_FIELD)
            };
        }

        const convertedValue = TimeFormatsConverter.offsetDateTimeStringToDayjs(value);
        if (!convertedValue) {
            return this.errorResult(t(StereotypesTranslationKey.VALUE_IS_NOT_VALID_DATE_TIME), attributeId);
        }

        const convertedDateTimeFrom = TimeFormatsConverter.offsetDateTimeStringToDayjs(dateTimeFrom);
        const convertedDateTimeTo = TimeFormatsConverter.offsetDateTimeStringToDayjs(dateTimeTo);

        if (convertedValue?.isBefore(convertedDateTimeFrom)) {
            return this.errorResult(t(StereotypesTranslationKey.VALUE_IS_BELLOW_ALLOWED_LIMIT), attributeId);
        }

        if (convertedValue.isAfter(convertedDateTimeTo)) {
            return this.errorResult(t(StereotypesTranslationKey.VALUE_IS_ABOVE_ALLOWED_LIMIT), attributeId);
        }

        return {};
    }

    private errorResult(error: string, attributeId: string) {
        return {
            [attributeId]: error
        };
    }

    getConstraintExpression(data: DateTimeRangeConstraintDataDto): string {
        const {dateTimeFrom, dateTimeTo} = data;
        if (dateTimeFrom === undefined && dateTimeTo === undefined) {
            return "";
        }

        const left = DateTimeFormatter.formatDateTime(dateTimeFrom) ?? "-∞";
        const right = DateTimeFormatter.formatDateTime(dateTimeTo) ?? "∞";

        return `<${left} - ${right}>`;
    }
}
